import { ForemanJobDetail } from '@ups/xplat/api/dto';
import {
  isHttpCacheIdentityScoped,
  isNativeScript,
  mergeObjectsCachedPriorityMut,
  type ArrayMergeSchema,
} from '@ups/xplat/utils';
import { catchError, Observable, of, take } from 'rxjs';
import type { ForemanJobService } from './foreman-job.service';
import { mergeSelectedJobIntoPersistedJobs } from './foreman-job-persistence.utils';
import {
  reconcileOfficeOwnedFieldsFromRemoteJob,
  reconcileOpenJobFromRemote,
} from './foreman-job-open-reconcile.utils';
import {
  reconcileLaborsFromRemoteJob,
  type RemoteLaborDraftReplacement,
} from './foreman-job-remote-labor-reconcile.utils';
import {
  dedupeForemanJobsByIdentity,
  getForemanJobDetailKey,
} from './foreman-job-key.utils';
import { dedupeSelfEntryEmployeeDispatches } from './foreman-individual-entry-employee.utils';
import { evictStaleCachedJobs } from './foreman-job-cache-eviction.utils';
import { registerForemanJobCachePurgeRetainer } from './foreman-job-cache-purge.utils';
import { captureForemanLaborCommittedState } from './foreman-labor-draft.utils';

// #33948: at module load, before any sign-in purge can run; see the function.
registerForemanJobCachePurgeRetainer();

/** Same key as ITE `getJobKey` / getForemanJobDetailKey (foremanJobID-first). */
const jobKeyFn = (j: ForemanJobDetail) => getForemanJobDetailKey(j);

function trimId(value: unknown): string {
  return value != null ? String(value).trim() : '';
}

/**
 * Index a job under both non-empty id fields so sparse jobID-only cache rows
 * still match a rich remote that carries foremanJobID-first (#30176 cache path).
 */
function registerJobAliases(
  aliasToIndex: Map<string, number>,
  job: ForemanJobDetail,
  index: number
): void {
  const f = trimId(job.foremanJobID);
  const j = trimId(job.jobID);
  if (f) {
    aliasToIndex.set(`f:${f}`, index);
  }
  if (j) {
    aliasToIndex.set(`j:${j}`, index);
  }
}

function findJobIndexByIdentity(
  aliasToIndex: Map<string, number>,
  job: ForemanJobDetail
): number | undefined {
  const f = trimId(job.foremanJobID);
  const j = trimId(job.jobID);
  if (f && aliasToIndex.has(`f:${f}`)) {
    return aliasToIndex.get(`f:${f}`);
  }
  if (j && aliasToIndex.has(`j:${j}`)) {
    return aliasToIndex.get(`j:${j}`);
  }
  return undefined;
}

export function getForemanJobList(
  service: ForemanJobService
): Observable<ForemanJobDetail[]> {
  return new Observable<ForemanJobDetail[]>((observer) => {
    const normalizeJobs = (data: ForemanJobDetail[]) =>
      data.map((job: ForemanJobDetail) => {
        job.employees?.forEach((employee) => {
          employee.labors?.forEach((labor) => {
            if (labor.isChanged === undefined || labor.isChanged === null) {
              labor.isChanged = false;
            }
            // A clean row matches the API, so its values become the baseline
            // the draft warning compares against (#30753).
            if (labor.isChanged !== true) {
              captureForemanLaborCommittedState(labor);
            }
          });
        });
        job.equipment?.forEach((equipment) => {
          equipment.usages?.forEach((usage) => {
            if (usage.isChanged === undefined || usage.isChanged === null) {
              usage.isChanged = false;
            }
          });
        });
        return job;
      });

    service.http
      .get(service.jobListApiEndpoint)
      .pipe(
        // `null` means the fetch FAILED, and is deliberately not `[]` (#33948).
        // Collapsing both into an empty array made a transport failure and an
        // authoritative online "you have no jobs" indistinguishable, so the one
        // case that most needs eviction — a fully revoked user — took the
        // offline branch and kept every cached job and crew roster (incl.
        // Last4SSN) on the device forever.
        catchError(() => {
          return of(null as ForemanJobDetail[] | null);
        })
      )
      .subscribe((response: ForemanJobDetail[] | null) => {
        const fetchFailed = response === null;
        const remote = response || [];
        service.remoteLaborDraftReplacements.set([]);

        // The offline job cache and cached-priority merge are a mobile
        // (NativeScript) offline-first concern. On web the server list is
        // authoritative — merging the cache here resurrects stale data
        // (e.g. cleared time segments reappearing after submit).
        if (!isNativeScript()) {
          // #35946: with no offline cache to fall back on, a failed fetch must
          // reach the caller as an error, never as an empty week.
          if (fetchFailed) {
            observer.error(new Error('Could not load your jobs.'));
            return;
          }
          observer.next(normalizeJobs(remote));
          observer.complete();
          return;
        }

        // No user owns the cache yet (#33947). `anon` is shared by whoever is
        // between sessions, so the job store (crew rosters, Last4SSN, drafts)
        // is neither read nor written under it: serve the server list as is.
        if (!isHttpCacheIdentityScoped()) {
          const jobs = dedupeForemanJobsByIdentity(remote);
          observer.next(
            normalizeJobs(jobs.map(dedupeSelfEntryEmployeeDispatches))
          );
          observer.complete();
          return;
        }

        // Read ONCE: the getter follows the live scope, and the write below
        // happens after an async read. A sign-out in between would otherwise
        // write this user's merged jobs and crew under the next scope.
        const jobsCacheKey = service.jobsCacheKey;

        // Only a FAILED fetch serves the cache untouched. An authoritative
        // empty list falls through to the merge + eviction path below, which is
        // what makes full revocation actually clear the device.
        if (fetchFailed) {
          service.offlineStorage
            .getItem(jobsCacheKey)
            .pipe(take(1))
            .subscribe((cachedData) => {
              const cached = (cachedData?.body as ForemanJobDetail[]) || [];
              // Offline emit: the store can hold self-entry duplicates written by
              // the persist path, so collapse them here too (#30177) — this branch
              // otherwise hands the cached array to consumers with no dedupe.
              observer.next(
                normalizeJobs(cached.map(dedupeSelfEntryEmployeeDispatches))
              );
              observer.complete();
            });
          return;
        }

        const forceClearJobIds = new Set(
          remote
            .filter((j) => j.forceClearLocalData === true)
            .map((j) => jobKeyFn(j))
            .filter(Boolean)
        );

        const cacheApiData = (data: ForemanJobDetail[]) => {
          // Producer write-side dedupe (#30176): one identity → one array element.
          // Then collapse duplicate self-entry rows per job (#30177) so the empty
          // phantom rows that block submit never reach the cache or any consumer.
          const deduped = dedupeForemanJobsByIdentity(data).map(
            dedupeSelfEntryEmployeeDispatches
          );
          const toStore = deduped.map((j) => {
            const { forceClearLocalData, ...rest } = j;
            void forceClearLocalData;
            return rest as ForemanJobDetail;
          });
          // A sign-out during the async read above released this scope and
          // purged it (#33948). Writing now would put the purged job list,
          // crew rosters included, straight back on disk under that scope.
          if (service.jobsCacheKey === jobsCacheKey) {
            service.offlineStorage
              .setItem(jobsCacheKey, {
                body: toStore,
                timestamp: Date.now(),
              })
              .pipe(take(1))
              .subscribe();
            // Bug 32137: the open job gets the same office-owned fields, or its next persist reverts them.
            const refreshed = reconcileOpenJobFromRemote(
              service.selectedJob(),
              remote
            );
            if (refreshed) service.selectedJob.set(refreshed);
          }
          observer.next(normalizeJobs(deduped));
          observer.complete();
        };

        // Entries written under the PRE-#33947 unscoped key are deliberately not
        // read through to, and are left intact on disk (#33993).
        //
        // An automatic rescue is not safely buildable: no per-row signal
        // attributes a draft to a user. `CreatedBy` on the dispatch row is who
        // created the DISPATCH — in a live payload it held 20+ distinct office
        // dispatcher ids plus nulls — and labor rows carry no `CreatedBy` at
        // all, so gating on it drops the common "hours on a dispatched crew"
        // draft entirely, while NOT gating on it adopts another user's
        // unsubmitted hours on a shared device. Both are worse than leaving the
        // entry where it is for a user-confirmed recovery.
        service.offlineStorage
          .getItem(jobsCacheKey)
          .pipe(take(1))
          .subscribe((cachedData) => {
            if (!cachedData?.body) {
              cacheApiData(remote);
              return;
            }
            const cached = cachedData.body as ForemanJobDetail[];
            const cachedForMerge = cached.filter(
              (j) => !forceClearJobIds.has(jobKeyFn(j))
            );
            const mergeResult = mergeJobsCachedPriorityWithResult(
              service.jobMergeSchema,
              [...cachedForMerge],
              remote
            );
            service.remoteLaborDraftReplacements.set(
              mergeResult.remoteLaborDraftReplacements
            );
            // Reconcile against the authoritative list (#33948). The merge above
            // only merges-into or pushes, so without this a job the server
            // stopped returning — and any orphaned employee row inside a job it
            // still returns — stayed on the device forever, kept feeding the
            // weekly summary and retained its crew roster after access ended.
            //
            // `remote` CAN legitimately be empty here, and that is the point:
            // only a FAILED fetch returns above, so an empty list at this line
            // means "you have no jobs" and must clear the device. That is the
            // full-revocation case — the worst one, where the user has been
            // removed from everything yet still holds every crew roster.
            // Hence `evictWhenRemoteEmpty` is passed explicitly rather than
            // defaulted: this caller is the one that separated a failed fetch
            // (`null`) from an authoritative empty (`[]`), so it is the one
            // entitled to say the empty list is authoritative.
            //
            // Unsynced local work is preserved by the pass itself.
            cacheApiData(
              evictStaleCachedJobs(mergeResult.jobs, remote, {
                evictWhenRemoteEmpty: true,
              })
            );
          });
      });
  });
}

/**
 * View a domain object as a string-keyed record. Interfaces carry no implicit
 * index signature, so passing one to a `Record<string, unknown>` parameter is a
 * compile error even though the runtime shape is exactly right. Kept as one named
 * helper so the widening is visible and searchable rather than an inline cast.
 */
function asMutableRecord(value: object): Record<string, unknown> {
  return value as unknown as Record<string, unknown>;
}

/**
 * Merge remote into cached with cached priority, matching rows by EITHER id
 * field (not only the single canonical key). A single keyFn map breaks when
 * cache holds jobID-only and remote is keyed foreman-first (#30176).
 */
export function mergeJobsCachedPriorityMut(
  jobMergeSchema: ArrayMergeSchema,
  cachedJobs: ForemanJobDetail[],
  remoteJobs: ForemanJobDetail[]
): ForemanJobDetail[] {
  return mergeJobsCachedPriorityWithResult(
    jobMergeSchema,
    cachedJobs,
    remoteJobs
  ).jobs;
}

export interface MergeJobsCachedPriorityResult {
  jobs: ForemanJobDetail[];
  remoteLaborDraftReplacements: RemoteLaborDraftReplacement[];
}

export function mergeJobsCachedPriorityWithResult(
  jobMergeSchema: ArrayMergeSchema,
  cachedJobs: ForemanJobDetail[],
  remoteJobs: ForemanJobDetail[]
): MergeJobsCachedPriorityResult {
  const remoteLaborDraftReplacements: RemoteLaborDraftReplacement[] = [];
  const aliasToIndex = new Map<string, number>();
  for (let i = 0; i < cachedJobs.length; i++) {
    registerJobAliases(aliasToIndex, cachedJobs[i], i);
  }

  for (const remote of remoteJobs) {
    const pos = findJobIndexByIdentity(aliasToIndex, remote);
    if (pos !== undefined) {
      const cachedValue = cachedJobs[pos];
      if (
        cachedValue &&
        typeof cachedValue === 'object' &&
        remote &&
        typeof remote === 'object'
      ) {
        // mergeObjectsCachedPriorityMut takes Record<string, unknown>, and an
        // interface with declared properties has no implicit index signature, so
        // ForemanJobDetail is not assignable to it (TS2345). The call mutates
        // cachedValue in place and its return is unused, so widening the view of
        // the two objects is safe and changes no runtime behaviour.
        mergeObjectsCachedPriorityMut(
          asMutableRecord(cachedValue),
          asMutableRecord(remote),
          jobMergeSchema
        );
      } else {
        cachedJobs[pos] = remote;
      }
      registerJobAliases(aliasToIndex, cachedJobs[pos], pos);
      continue;
    }
    const idx = cachedJobs.length;
    cachedJobs.push(remote);
    registerJobAliases(aliasToIndex, remote, idx);
  }

  for (const job of cachedJobs) {
    const remoteJob = remoteJobs.find(
      (r) =>
        (trimId(job.foremanJobID) &&
          trimId(job.foremanJobID) === trimId(r.foremanJobID)) ||
        (trimId(job.jobID) && trimId(job.jobID) === trimId(r.jobID))
    );
    if (remoteJob) {
      reconcileOfficeOwnedFieldsFromRemoteJob(job, remoteJob);
      remoteLaborDraftReplacements.push(
        ...reconcileLaborsFromRemoteJob(job, remoteJob)
      );
    }
  }
  return {
    jobs: cachedJobs,
    remoteLaborDraftReplacements,
  };
}

export function persistSelectedJobSnapshot(
  service: ForemanJobService,
  selectedJobOverride?: ForemanJobDetail | null
) {
  const selectedJob = selectedJobOverride ?? service.selectedJob();

  if (!service.getPersistedJobs || !service.persistJobChanges || !selectedJob) {
    return;
  }

  const mergedJobs = mergeSelectedJobIntoPersistedJobs(
    service.getPersistedJobs(),
    selectedJob
  );

  service.persistJobChanges(mergedJobs);
}
